import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    MenuItem,
    Box,
    InputLabel,
    NativeSelect,
    FormControl,
    FormLabel,
    Radio,
    FormControlLabel,
    RadioGroup
} from "@mui/material";
import { useSnackbar } from "notistack";
import http, { ROUTES } from 'src/utils/httpHelper';
import InvoiceCombineKey from "src/models/invoiceCombineKey";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import GeneralAutocomplete from "src/components/GeneralAutocomplete";

interface InvoiceCombineKeyFormProps {
    modalCloseCallback,
    onSuccessCallback
    item?: InvoiceCombineKey
}

const InvoiceCombineKeyForm = ({ modalCloseCallback, onSuccessCallback, item }: InvoiceCombineKeyFormProps) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, setValue, trigger, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(InvoiceCombineKey.getRules(item)),
        defaultValues: InvoiceCombineKey.getInitValues(item)
    });

    const [clientOptions, setClientOptions] = useState([]);
    const [isFormDataLoadingClient, setIsFormDataLoadingClient] = useState(false);
    useEffect(() =>{
        setIsFormDataLoadingClient(true);
        http.get(ROUTES.BASE + '/api/select-options/clients')
            .then(response => {
                setClientOptions(response.data);
            })
            .catch(error => console.error(error))
            .finally(()=>setIsFormDataLoadingClient(false));
    }, []);

    const [expeditorOptions, setExpeditorOptions] = useState([]);
    const [isFormDataLoadingExpeditor, setIsFormDataLoadingExpeditor] = useState(false);
    useEffect(() =>{
        setIsFormDataLoadingExpeditor(true);
        http.get(ROUTES.BASE + '/api/select-options/invoice-expeditors')
            .then(response => {
                setExpeditorOptions(response.data);
            })
            .catch(error => console.error(error))
            .finally(()=>setIsFormDataLoadingExpeditor(false));
    }, []);

    return (
        <form noValidate onSubmit={handleSubmit(async (values) => {
            await http.request({
                method: item ? 'put' : 'post',
                url: item ? `/api/invoice-combine-keys/${item.id}` : '/api/invoice-combine-keys',
                data: values
            })
            .then((response) => {
                onSuccessCallback();
                enqueueSnackbar(response.data.message, { variant: 'success' });
            })
            .catch((error) => {
                console.error(error);
                enqueueSnackbar(error.message, { variant: 'error' });
            });
        })}>
            <DialogContent
                dividers
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <GeneralAutocomplete
                            control={control}
                            formField="client_id"
                            label={t("FIELDS.CLIENT")}
                            isFormDataLoading={isFormDataLoadingClient}
                            options={clientOptions}
                            setValue={setValue}
                            trigger={trigger}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <GeneralAutocomplete
                            control={control}
                            formField="invoice_expeditor_id"
                            label={t("FIELDS.EXPEDITOR")}
                            isFormDataLoading={isFormDataLoadingExpeditor}
                            options={expeditorOptions}
                            setValue={setValue}
                            trigger={trigger}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="identifier"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.identifier)}
                                    fullWidth
                                    label={t("FIELDS.IDENTIFIER")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.description)}
                                    fullWidth
                                    label={t("FIELDS.DESCRIPTION")}
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
            >
                <Box>
                    <Button color="secondary" onClick={modalCloseCallback}>
                        {t("GENERAL.CANCEL")}
                    </Button>
                    <Button
                        sx={{
                            ml: 2
                        }}
                        type="submit"
                        startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={isSubmitting || !isValid}
                        variant="contained"
                    >
                        {t(!item ? "INVOICE-COMBINE-KEYS.CREATE" : 'INVOICE-COMBINE-KEYS.EDIT')}
                    </Button>
                </Box>
            </DialogActions>
        </form>
    )
}

export default InvoiceCombineKeyForm;